export interface AccentPreset {
  key: string;
  label: string;
  top: string;    // gradient top color
  bottom: string; // gradient bottom color
  glow: string;   // radial glow color (usually translucent white)
}

// A rotating palette of light backgrounds. The Generate tab cycles through
// these automatically (one per generated pack), so marketers never pick one.
export const ACCENT_PRESETS: AccentPreset[] = [
  { key: 'indigo',   label: 'Indigo',   top: '#eef1ff', bottom: '#dbe2ff', glow: 'rgba(255,255,255,0.95)' },
  { key: 'purple',   label: 'Purple',   top: '#f6eeff', bottom: '#e7dbff', glow: 'rgba(255,255,255,0.95)' },
  { key: 'teal',     label: 'Teal',     top: '#eafcff', bottom: '#d6f2f7', glow: 'rgba(255,255,255,0.95)' },
  { key: 'rose',     label: 'Rose',     top: '#fff0f3', bottom: '#ffdbe4', glow: 'rgba(255,255,255,0.95)' },
  { key: 'amber',    label: 'Amber',    top: '#fff8e8', bottom: '#ffe9c2', glow: 'rgba(255,255,255,0.95)' },
  { key: 'mint',     label: 'Mint',     top: '#ecfff4', bottom: '#d2f6e2', glow: 'rgba(255,255,255,0.95)' },
  { key: 'sky',      label: 'Sky',      top: '#eaf6ff', bottom: '#d0e8ff', glow: 'rgba(255,255,255,0.95)' },
  { key: 'lavender', label: 'Lavender', top: '#f3eeff', bottom: '#e0d6ff', glow: 'rgba(255,255,255,0.95)' },
  { key: 'peach',    label: 'Peach',    top: '#fff1ea', bottom: '#ffdcc9', glow: 'rgba(255,255,255,0.95)' },
  { key: 'lime',     label: 'Lime',     top: '#f4ffe8', bottom: '#e0f5c2', glow: 'rgba(255,255,255,0.95)' },
  { key: 'cyan',     label: 'Cyan',     top: '#e8feff', bottom: '#c9f0f5', glow: 'rgba(255,255,255,0.95)' },
  { key: 'pink',     label: 'Pink',     top: '#fff0fa', bottom: '#ffd6f0', glow: 'rgba(255,255,255,0.95)' },
  { key: 'slate',    label: 'Slate',    top: '#f2f5f9', bottom: '#dfe6ef', glow: 'rgba(255,255,255,0.95)' },
  { key: 'coral',    label: 'Coral',    top: '#fff0ee', bottom: '#ffd7d0', glow: 'rgba(255,255,255,0.95)' },
  { key: 'violet',   label: 'Violet',   top: '#f4f0ff', bottom: '#e3d9ff', glow: 'rgba(255,255,255,0.95)' },
  { key: 'aqua',     label: 'Aqua',     top: '#e9fffb', bottom: '#ccf3ea', glow: 'rgba(255,255,255,0.95)' },
  { key: 'sand',     label: 'Sand',     top: '#fbf6ee', bottom: '#efe2cc', glow: 'rgba(255,255,255,0.95)' },
  { key: 'blue',     label: 'Blue',     top: '#edf1ff', bottom: '#d3ddff', glow: 'rgba(255,255,255,0.95)' },
  { key: 'plum',     label: 'Plum',     top: '#f9eefb', bottom: '#ecd6f2', glow: 'rgba(255,255,255,0.95)' },
  { key: 'seafoam',  label: 'Seafoam',  top: '#ecfffa', bottom: '#d0f5ec', glow: 'rgba(255,255,255,0.95)' },
];

export interface RenderOpts {
  preset: AccentPreset;
  caption?: string;
}

// Reads intrinsic pixel size from any CanvasImageSource-like object.
export function sourceSize(img: CanvasImageSource): { w: number; h: number } {
  const a = img as unknown as { naturalWidth?: number; naturalHeight?: number; width?: number; height?: number };
  return {
    w: a.naturalWidth ?? a.width ?? 0,
    h: a.naturalHeight ?? a.height ?? 0,
  };
}

// Computes the source rectangle to draw `img` so it COVERS a dw×dh area,
// preserving aspect ratio and anchoring to the top (sy=0) so screenshot
// chrome at the top stays visible.
export function coverSource(imgW: number, imgH: number, dw: number, dh: number): { sx: number; sy: number; sw: number; sh: number } {
  if (imgW <= 0 || imgH <= 0) return { sx: 0, sy: 0, sw: imgW, sh: imgH };
  const scale = Math.max(dw / imgW, dh / imgH);
  const sw = dw / scale;
  const sh = dh / scale;
  const sx = (imgW - sw) / 2;
  const sy = 0; // top-anchored
  return { sx, sy, sw, sh };
}

export type OutputFormat = 'phone' | 'banner';

export const OUTPUT_META: Record<OutputFormat, { label: string; width: number; height: number }> = {
  phone: { label: 'Phone screenshot', width: 1080, height: 1920 },
  banner: { label: 'Feature banner', width: 1024, height: 500 },
};

// Landscape ("album") phone screenshot — used automatically when the uploaded
// screenshot is wider than it is tall (landscape games, tablet listings).
export const PHONE_LANDSCAPE = { width: 1920, height: 1080 };

const PHONE_ASPECT = 640 / 1300; // body short-side / long-side

// True when the source image is wider than it is tall, i.e. an album shot.
export function isLandscapeSource(img: CanvasImageSource): boolean {
  const { w, h } = sourceSize(img);
  return w > h;
}

function makeCanvas(w: number, h: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D | null } {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  return { canvas, ctx: canvas.getContext('2d') };
}

function roundedRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function drawBackground(ctx: CanvasRenderingContext2D, w: number, h: number, preset: AccentPreset): void {
  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, preset.top);
  g.addColorStop(1, preset.bottom);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
}

function drawGlow(ctx: CanvasRenderingContext2D, cx: number, cy: number, radius: number, color: string): void {
  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
  g.addColorStop(0, color);
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
}

// Draws a dark phone frame at (x,y,w,h) and pastes `img` into the screen area
// (cover fit) through a rounded clip mask. Screenshot pixels are never scaled
// non-uniformly, so text stays crisp.
function drawPhone(ctx: CanvasRenderingContext2D, img: CanvasImageSource, x: number, y: number, w: number, h: number): void {
  // Radius/bezel scale off the SHORT side so a landscape (wide) frame keeps the
  // same proportions as a portrait one instead of over-rounding.
  const landscape = w > h;
  const short = Math.min(w, h);
  const bodyRadius = short * 0.11;
  const bezel = short * 0.035;

  // Drop shadow + body.
  ctx.save();
  ctx.shadowColor = 'rgba(30,35,60,0.28)';
  ctx.shadowBlur = short * 0.12;
  ctx.shadowOffsetY = h * 0.03;
  ctx.fillStyle = '#111318';
  roundedRectPath(ctx, x, y, w, h, bodyRadius);
  ctx.fill();
  ctx.restore();

  // Screen area.
  const sx = x + bezel;
  const sy = y + bezel;
  const sw = w - bezel * 2;
  const sh = h - bezel * 2;
  const screenRadius = bodyRadius - bezel * 0.6;

  ctx.save();
  roundedRectPath(ctx, sx, sy, sw, sh, screenRadius);
  ctx.clip();
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(sx, sy, sw, sh);
  const { w: iw, h: ih } = sourceSize(img);
  if (iw > 0 && ih > 0) {
    const src = coverSource(iw, ih, sw, sh);
    ctx.drawImage(img, src.sx, src.sy, src.sw, src.sh, sx, sy, sw, sh);
  }
  ctx.restore();

  // Camera notch pill: top-center in portrait, or centered on the left short
  // edge (as the phone is held) in landscape.
  ctx.fillStyle = '#000000';
  if (landscape) {
    const notchW = short * 0.028;
    const notchH = short * 0.16;
    roundedRectPath(ctx, x + bezel * 0.5, y + h / 2 - notchH / 2, notchW, notchH, notchW / 2);
  } else {
    const notchW = short * 0.16;
    const notchH = short * 0.028;
    roundedRectPath(ctx, x + w / 2 - notchW / 2, y + bezel * 0.5, notchW, notchH, notchH / 2);
  }
  ctx.fill();
}

// Draws a white rounded "pill" containing `text`, centered horizontally at cx,
// with its vertical center at cy. Returns nothing.
function drawCaptionPill(ctx: CanvasRenderingContext2D, text: string, cx: number, cy: number, fontPx: number): void {
  ctx.font = `600 ${fontPx}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const metrics = ctx.measureText(text);
  const padX = fontPx * 0.9;
  const padY = fontPx * 0.55;
  const pillW = metrics.width + padX * 2;
  const pillH = fontPx + padY * 2;

  ctx.save();
  ctx.shadowColor = 'rgba(30,35,60,0.18)';
  ctx.shadowBlur = fontPx * 0.6;
  ctx.shadowOffsetY = fontPx * 0.15;
  ctx.fillStyle = '#ffffff';
  roundedRectPath(ctx, cx - pillW / 2, cy - pillH / 2, pillW, pillH, pillH / 2);
  ctx.fill();
  ctx.restore();

  ctx.fillStyle = '#1b1e2b';
  ctx.fillText(text, cx, cy);
}

// Wraps `text` to `maxWidth` and draws it centered on (cx, cy): each line is
// horizontally centered at cx, and the whole block is vertically centered
// around cy regardless of how many lines it wraps to. Bold. Returns nothing.
function drawHeadline(ctx: CanvasRenderingContext2D, text: string, cx: number, cy: number, maxWidth: number, fontPx: number, color: string): void {
  ctx.font = `700 ${fontPx}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = color;
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  const lineH = fontPx * 1.2;
  const startY = cy - ((lines.length - 1) * lineH) / 2;
  lines.forEach((l, i) => ctx.fillText(l, cx, startY + i * lineH));
}

export function renderPhoneScreenshot(img: CanvasImageSource, opts: RenderOpts): HTMLCanvasElement {
  // Portrait vs landscape canvas is chosen from the screenshot's own shape.
  const landscape = isLandscapeSource(img);
  const { width: W, height: H } = landscape ? PHONE_LANDSCAPE : OUTPUT_META.phone;
  const { canvas, ctx } = makeCanvas(W, H);
  if (!ctx) return canvas;

  drawBackground(ctx, W, H, opts.preset);
  drawGlow(ctx, W / 2, H * 0.52, Math.min(W, H) * 0.75, opts.preset.glow);

  // In landscape the frame is width-bound; in portrait it's height-bound.
  const phoneW = landscape ? W * 0.82 : H * 0.74 * PHONE_ASPECT;
  const phoneH = landscape ? phoneW * PHONE_ASPECT : H * 0.74;
  const phoneX = (W - phoneW) / 2;
  const phoneY = (H - phoneH) / 2 + H * 0.03;
  drawPhone(ctx, img, phoneX, phoneY, phoneW, phoneH);

  const caption = opts.caption?.trim();
  if (caption) drawCaptionPill(ctx, caption, W / 2, landscape ? H * 0.11 : H * 0.075, 54);

  return canvas;
}

export function renderBanner(img: CanvasImageSource, opts: RenderOpts): HTMLCanvasElement {
  const { width: W, height: H } = OUTPUT_META.banner;
  const { canvas, ctx } = makeCanvas(W, H);
  if (!ctx) return canvas;

  drawBackground(ctx, W, H, opts.preset);

  // A landscape first-screenshot gets a wide phone (shorter, so it still clears
  // the banner height); a portrait one keeps the tall phone on the right.
  const landscape = isLandscapeSource(img);
  const phoneH = landscape ? H * 0.62 : H * 0.86;
  const phoneW = landscape ? phoneH / PHONE_ASPECT : phoneH * PHONE_ASPECT;
  const phoneX = W - phoneW - W * (landscape ? 0.05 : 0.08);
  const phoneY = (H - phoneH) / 2;
  drawGlow(ctx, phoneX + phoneW / 2, H / 2, H * 0.7, opts.preset.glow);
  drawPhone(ctx, img, phoneX, phoneY, phoneW, phoneH);

  const caption = opts.caption?.trim();
  if (caption) {
    // Center the headline within the left column (between the left margin and
    // the phone), both horizontally and vertically.
    const colLeft = W * 0.07;
    const colRight = phoneX - W * 0.04;
    const cx = (colLeft + colRight) / 2;
    const textMaxW = colRight - colLeft;
    drawHeadline(ctx, caption, cx, H / 2, textMaxW, 62, '#1b1e2b');
  }

  return canvas;
}
